import { ClipboardListIcon, TableIcon } from '@heroicons/react/outline';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import React, { useEffect, useRef, useState } from 'react';
import { db, useOutsideClick } from '../../firebase';
import WorkflowPDF from './WorkflowPDF';
import { CSVLink } from 'react-csv';
import ColName from './ColName';
import BigIdDropdown from '../../components/BigIdDropdown';

function Workflow({ selectedCompany }) {
  const [years, setYears] = useState([]);
  const [subs, setSubs] = useState([]);

  const [selectedYear, setSelectedYear] = useState();
  const [selectedSubs, setSelectedSubs] = useState([]);

  const [yearsVisible, setYearsVisible] = useState(false);
  const [subsidiariesVisible, setSubsidiariesVisible] = useState(false);

  const [tableCols, setTableCols] = useState([]);
  const [tableVals, setTableVals] = useState([]);
  const [tableSubs, setTableSubs] = useState([]);
  const [tableProgress, setTableProgress] = useState([]);
  const [tableFilled, setTableFilled] = useState([]);
  const [totalFields, setTotalFields] = useState(0);
  const [categoryFillData, setCategoryFillData] = useState([]);

  const subsidiariesRef = useRef();
  useOutsideClick(subsidiariesRef, () => setSubsidiariesVisible(false));

  // Table States

  /// Set Webpage Title

  useEffect(() => {
    document.title = 'Workflow - Get an Overview over current Status';
  }, []);

  /// Load Document Types

  useEffect(() => {
    async function fetchYears() {
      const newYears = await getDocs(collection(db, 'user_data', selectedCompany?.id, 'years'));
      setYears(newYears.docs);
    }

    fetchYears();
  }, [selectedCompany]);

  /// Document Type Selection -> Fetch Form and Documents, create Color List ///

  // const answerOpts = useState([])

  async function selectYear(year) {
    setYearsVisible(false);
    setSelectedYear(year);

    var existingFields = 0;

    const yearForm = await getDoc(
      doc(db, 'user_data', selectedCompany?.id, 'years', year.id, 'forms', 'standard_form')
    );

    // Counting Fields
    yearForm.data().categories.forEach((category) => {
      existingFields += category.fields.length;
    });

    const fetchedSubs = await getDocs(
      collection(db, 'user_data', selectedCompany?.id, 'years', year.id, 'subsidaries')
    );
    const subsidiaries = fetchedSubs.docs.map((sub, index) => sub.id);
    const progress = fetchedSubs.docs.map(
      (sub, index) => sub.data().fieldValues.filter((fieldValue) => fieldValue.done === true).length
    );
    const filled = fetchedSubs.docs.map(
      (sub, index) =>
        sub.data().fieldValues.filter((fieldValue) => isFieldFilled(fieldValue)).length
    );

    const workflowCols = yearForm
      .data()
      .categories.find((category) => category.name === 'Workflow')?.fields;

    if (workflowCols) {
      const workflowVals = fetchedSubs.docs.map((sub, index) => {
        const row = [];

        for (const col of workflowCols) {
          const val = sub.data().fieldValues.find((value) => value.id === col.id);
          if (val) {
            row.push(val);
          } else {
            row.push('');
          }
        }

        return row;
      });

      setTableVals(workflowVals);
      setTableCols(workflowCols);
    } else {
      setTableVals([]);
      setTableCols([]);
    }

    // Calculate subsidiary-based category fill data
    const categories = yearForm.data().categories;
    const categoryData = fetchedSubs.docs.map((sub) => {
      const subData = {
        code: sub.id,
        categories: {},
      };

      categories.forEach((category) => {
        const categoryFieldIds = category.fields.map((field) => field.id);
        const categoryFieldCount = category.fields.length;

        // Calculate filled and final count for this category for this subsidiary
        const filledInThisCategory = sub
          .data()
          .fieldValues.filter(
            (fieldValue) => categoryFieldIds.includes(fieldValue.id) && isFieldFilled(fieldValue)
          ).length;

        const finalInThisCategory = sub
          .data()
          .fieldValues.filter(
            (fieldValue) => categoryFieldIds.includes(fieldValue.id) && fieldValue.done === true
          ).length;

        const fillPercentage =
          categoryFieldCount > 0 ? (filledInThisCategory / categoryFieldCount) * 100 : 0;
        const finalPercentage =
          categoryFieldCount > 0 ? (finalInThisCategory / categoryFieldCount) * 100 : 0;

        subData.categories[category.name] = {
          filled: filledInThisCategory,
          final: finalInThisCategory,
          total: categoryFieldCount,
          percentage: fillPercentage,
          finalPercentage: finalPercentage,
        };
      });

      return subData;
    });

    // Calculcate Progress of every subsidiary

    setTableSubs(subsidiaries);
    setSelectedSubs(subsidiaries);
    setTableProgress(progress);
    setTableFilled(filled);
    setTotalFields(existingFields);
    setCategoryFillData(categoryData);
  }

  function evalProgressColor(progress) {
    if (progress === 0) {
      return ' bg-red-300 border-red-300';
    } else if (progress / totalFields < 0.35) {
      return ' bg-red-200 border-red-200';
    } else if (progress / totalFields < 0.5) {
      return ' bg-orange-200 ';
    } else if (progress / totalFields < 0.7) {
      return ' bg-yellow-200 border-yellow-200';
    } else if (progress / totalFields < 0.9) {
      return ' bg-green-200 border-green-200';
    } else if (progress / totalFields < 1) {
      return ' bg-green-300 border-green-300';
    } else {
      return ' bg-green-400 border-green-400';
    }
  }

  // Normalize possible non-string inputs from imports and answers
  function normalizeAnswer(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (Array.isArray(value)) return value.join(',');
    if (typeof value === 'object') {
      if ('value' in value) return normalizeAnswer(value.value);
      try {
        return JSON.stringify(value);
      } catch (e) {
        return '';
      }
    }
    return '';
  }

  function isFieldFilled(fieldValue) {
    if (!fieldValue) return false;
    const ans = normalizeAnswer(fieldValue.answer).trim();
    const amt = normalizeAnswer(fieldValue.transaction_amount).trim();
    return ans !== '' || amt !== '';
  }

  /// Preparing Csv Download ///

  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvData, setCsvData] = useState([]);

  async function prepCsvDownload() {
    const cols = tableCols;
    const vals = tableVals;
    const subs = tableSubs;
    const progress = tableProgress;
    const filled = tableFilled;

    const headers = [{ label: 'Code', key: 'Code' }];
    const data = [];

    // Setting Headers
    for (const col of cols) {
      const row = {};
      row.label = col.topic;
      row.key = col.topic;
      headers.push(row);
    }
    headers.push({ label: 'Filled', key: 'Filled' });
    headers.push({ label: 'Final', key: 'Final' });

    // Add category headers
    if (categoryFillData && categoryFillData.length > 0 && categoryFillData[0]) {
      // Add filled headers for each category
      Object.keys(categoryFillData[0].categories).forEach((categoryName) => {
        headers.push({ label: `${categoryName} Filled`, key: `${categoryName}_filled` });
      });
      // Add final headers for each category
      Object.keys(categoryFillData[0].categories).forEach((categoryName) => {
        headers.push({ label: `${categoryName} Final`, key: `${categoryName}_final` });
      });
    }

    // Setting Data
    for (const [i, valRow] of Object.entries(vals)) {
      if (!selectedSubs.includes(subs[i])) {
        continue;
      }

      const row = {};

      row['Code'] = subs[i];

      for (const [j, val] of Object.entries(valRow)) {
        row[cols[j].topic] = normalizeAnswer(
          val && typeof val === 'object' ? val.answer : val
        );
      }

      row['Filled'] = filled[i] + '/' + totalFields;
      row['Final'] = progress[i] + '/' + totalFields;

      // Add category data to the same row
      if (categoryFillData && categoryFillData.length > 0) {
        const subCategoryData = categoryFillData.find((sub) => sub.code === subs[i]);
        if (subCategoryData) {
          // Add filled data for each category
          Object.entries(subCategoryData.categories).forEach(([categoryName, categoryInfo]) => {
            row[`${categoryName}_filled`] =
              `${categoryInfo.filled}/${categoryInfo.total} (${categoryInfo.percentage.toFixed(1)}%)`;
          });
          // Add final data for each category
          Object.entries(subCategoryData.categories).forEach(([categoryName, categoryInfo]) => {
            row[`${categoryName}_final`] =
              `${categoryInfo.final}/${categoryInfo.total} (${categoryInfo.finalPercentage.toFixed(1)}%)`;
          });
        }
      }

      data.push(row);
    }

    setCsvData(data);
    setCsvHeaders(headers);
  }

  useEffect(() => {
    if (selectedSubs) {
      prepCsvDownload();
    }
  }, [selectedSubs]);

  const tableElement = useRef();

  return (
    <div className="">
      <div className="w-[100%] bg-white font-body relative">
        <div className="flex items-center pb-6 border-b fixed w-[100%] z-20 p-4 bg-white">
          <div className="p-1.5 rounded-full bg-teal-400 text-white w-max">
            <ClipboardListIcon className="w-8 h-8" />
          </div>
          <div>
            <span className="text-2xl p-1 ml-3">Workflow</span>
          </div>
          <div className="ml-auto mr-0 flex">
            {/* Export Buttons */}

            <CSVLink
              separator={';'}
              data={csvData}
              headers={csvHeaders}
              className="flex items-center mr-2 rounded-lg bg-teal-400 text-white px-4 transition-all hover:bg-teal-500"
            >
              <div className="mr-1">
                <TableIcon className="w-5 h-5" />
              </div>
              CSV
            </CSVLink>
            <WorkflowPDF
              tableCols={tableCols}
              vals={tableVals}
              subs={tableSubs}
              progress={tableProgress}
              filled={tableFilled}
              totalFields={totalFields}
              selectedSubs={selectedSubs}
              categoryFillData={categoryFillData}
            />

            {/* Document Type and Document Selection */}

            <BigIdDropdown
              placeholder={'Document Type'}
              width={'12rem'}
              color={'teal'}
              selectedOption={selectedYear}
              selectFunction={selectYear}
              options={years}
              dropdownVisible={yearsVisible}
              setDropdownVisible={setYearsVisible}
            />
            <div ref={subsidiariesRef} className="relative mx-1 w-36">
              <button
                className="w-[100%] border rounded-lg py-2 hover:bg-neutral-50 transition-all"
                onClick={() => setSubsidiariesVisible((prev) => !prev)}
              >
                {selectedYear
                  ? tableSubs?.sort().join(',') === selectedSubs?.sort().join(',') &&
                    selectedSubs?.length
                    ? 'All Documents'
                    : selectedSubs?.length + ' Documents'
                  : 'Documents'}
              </button>
              <div className="absolute bg-white w-[100%] px-1 drop-shadow-lg rounded-b-lg z-20 max-h-60 overflow-y-auto">
                <div className={subsidiariesVisible ? '' : 'hidden'}>
                  <button
                    className={
                      'w-[100%] p-1 my-1 hover:bg-teal-400 hover:text-white rounded-md transition' +
                      (tableSubs?.sort().join(',') === selectedSubs?.sort().join(',')
                        ? ' bg-teal-400 text-white'
                        : '')
                    }
                    onClick={() => {
                      setSelectedSubs((prev) =>
                        tableSubs?.sort().join(',') === prev?.sort().join(',') ? [] : tableSubs
                      );
                    }}
                  >
                    All Documents
                  </button>
                  {tableSubs?.map((sub, index) => (
                    <button
                      onClick={() =>
                        setSelectedSubs((prev) =>
                          prev.includes(sub)
                            ? prev.filter((prevSub) => prevSub !== sub)
                            : [...prev, sub]
                        )
                      }
                      key={index}
                      className={
                        'w-[100%] p-1 mb-0.5 hover:bg-teal-400 hover:text-white rounded-md transition ' +
                        (selectedSubs.includes(sub) ? ' bg-teal-400 text-white' : '')
                      }
                    >
                      {sub}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}

        {selectedYear && totalFields && (
          <div className="top-[105px] relative z-10 px-4 pb-4 min-h-screen">
            <table ref={tableElement} className="w-[100%]">
              <thead>
                <tr className="border">
                  {['Code', ...tableCols.map((col) => col.topic), 'Filled', 'Final'].map(
                    (col, index) => (
                      <ColName
                        key={index}
                        tableElement={tableElement}
                        cellContent={col}
                        index={index}
                      />
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {tableSubs.map(
                  (tableSub, i) =>
                    selectedSubs.includes(tableSubs[i]) && (
                      <tr key={i} className="border">
                        <th className="border text-sm bg-neutral-100">{tableSub}</th>
                        {tableVals[i]?.map((val, j) => (
                          <th
                            key={j}
                            className="border text-sm bg-neutral-100"
                            style={
                              tableCols?.[j]?.answer_possibilities?.find(
                                (opt) =>
                                  opt.opt ===
                                  normalizeAnswer(
                                    val && typeof val === 'object' ? val.answer : val
                                  )
                              )?.color && {
                                backgroundColor: tableCols?.[j]?.answer_possibilities?.find(
                                  (opt) =>
                                    opt.opt ===
                                    normalizeAnswer(
                                      val && typeof val === 'object' ? val.answer : val
                                    )
                                )?.color,
                                borderColor: tableCols?.[j]?.answer_possibilities?.find(
                                  (opt) =>
                                    opt.opt ===
                                    normalizeAnswer(
                                      val && typeof val === 'object' ? val.answer : val
                                    )
                                )?.color,
                              }
                            }
                          >
                            {normalizeAnswer(
                              val && typeof val === 'object' ? val.answer : val
                            )}
                          </th>
                        ))}
                        <th className={'border text-sm' + evalProgressColor(tableFilled[i])}>
                          {tableFilled[i] +
                            ' / ' +
                            totalFields +
                            ' (' +
                            ((tableFilled[i] / totalFields) * 100).toFixed(1) +
                            '%)'}
                        </th>
                        <th className={'border text-sm' + evalProgressColor(tableProgress[i])}>
                          {tableProgress[i] +
                            ' / ' +
                            totalFields +
                            ' (' +
                            ((tableProgress[i] / totalFields) * 100).toFixed(1) +
                            '%)'}
                        </th>
                      </tr>
                    )
                )}
              </tbody>
            </table>

            {/* Category Breakdown Section */}
            {categoryFillData && categoryFillData.length > 0 && (
              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">
                  Category Fill Breakdown by Subsidiary
                </h3>
                <table className="w-[100%]">
                  <thead>
                    <tr className="border">
                      <th className="border text-sm bg-neutral-100 p-2 text-left">Code</th>
                      {categoryFillData[0] &&
                        Object.keys(categoryFillData[0].categories).map((categoryName, index) => (
                          <th key={index} className="border text-sm bg-neutral-100 p-2 text-center">
                            {categoryName}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {categoryFillData
                      .filter((subData) => selectedSubs.includes(subData.code))
                      .map((subData, index) => (
                        <tr key={index} className="border">
                          <td className="border text-sm p-2 bg-neutral-100">{subData.code}</td>
                          {Object.entries(subData.categories).map(
                            ([categoryName, categoryInfo], catIndex) => (
                              <td
                                key={catIndex}
                                className={
                                  'border text-sm p-2 text-center' +
                                  evalProgressColor((categoryInfo.percentage / 100) * totalFields)
                                }
                              >
                                {categoryInfo.filled}/{categoryInfo.total} (
                                {categoryInfo.percentage.toFixed(1)}%)
                              </td>
                            )
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Category Final Breakdown Section */}
            {categoryFillData && categoryFillData.length > 0 && (
              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">
                  Category Final Breakdown by Subsidiary
                </h3>
                <table className="w-[100%]">
                  <thead>
                    <tr className="border">
                      <th className="border text-sm bg-neutral-100 p-2 text-left">Code</th>
                      {categoryFillData[0] &&
                        Object.keys(categoryFillData[0].categories).map((categoryName, index) => (
                          <th key={index} className="border text-sm bg-neutral-100 p-2 text-center">
                            {categoryName}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {categoryFillData
                      .filter((subData) => selectedSubs.includes(subData.code))
                      .map((subData, index) => (
                        <tr key={index} className="border">
                          <td className="border text-sm p-2 bg-neutral-100">{subData.code}</td>
                          {Object.entries(subData.categories).map(
                            ([categoryName, categoryInfo], catIndex) => (
                              <td
                                key={catIndex}
                                className={
                                  'border text-sm p-2 text-center' +
                                  evalProgressColor(
                                    (categoryInfo.finalPercentage / 100) * totalFields
                                  )
                                }
                              >
                                {categoryInfo.final}/{categoryInfo.total} (
                                {categoryInfo.finalPercentage.toFixed(1)}%)
                              </td>
                            )
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Workflow;
